import {
  useImperativeHandle,
  useRef,
  useState,
  type FC,
  type Ref
} from "react";

import { cn } from "@/lib/utils";

import type { Tutorial } from "../types";
import { PlayDisc } from "./PlayDisc";

type Props = {
  tutorial: Tutorial;
  /** Seconds in to start at, for a chapter link. */
  startTime?: number;
  onTimeUpdate?: (secs: number) => void;
  onPlay?: () => void;
  className?: string;
  ref?: Ref<HTMLVideoElement>;
};

/**
 * The tutorial player, after cursor.com/learn: the poster with a round play
 * button until it starts, then the browser's controls.
 */
export const TutorialVideo: FC<Props> = ({
  tutorial,
  startTime = 0,
  onTimeUpdate,
  onPlay,
  className,
  ref
}) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const [started, setStarted] = useState(false);
  useImperativeHandle(ref, () => videoRef.current as HTMLVideoElement, []);

  return (
    <div
      className={cn(
        "relative aspect-video w-full overflow-hidden bg-black",
        className
      )}
    >
      {/* Narrated walkthrough; the chapters list is its outline. */}
      {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
      <video
        ref={videoRef}
        src={tutorial.src}
        poster={tutorial.poster}
        controls={started}
        playsInline
        preload="metadata"
        className="size-full"
        onLoadedMetadata={(e) => {
          if (startTime) e.currentTarget.currentTime = startTime;
        }}
        onPlay={() => {
          setStarted(true);
          onPlay?.();
        }}
        onTimeUpdate={(e) => onTimeUpdate?.(e.currentTarget.currentTime)}
        data-testid="tutorial-video"
      />
      {started ? null : (
        <button
          type="button"
          aria-label={`Play ${tutorial.title}`}
          onClick={() => {
            setStarted(true);
            videoRef.current?.play().catch(() => undefined);
          }}
          className="group/play absolute inset-0 flex cursor-pointer items-center justify-center outline-none"
        >
          <PlayDisc />
        </button>
      )}
    </div>
  );
};
